'use client';

import { useEffect } from 'react';

const DEFAULT_SECTION_IDS = [
  'intro',
  'growing-methods',
  'trusted-partner',
  'software-platform',
  'system-overview-mobile',
  'system-overview-desktop',
  'benefits',
  'cta',
  'gallery',
  'why-pulselogic',
  'why-radsum',
  'figure',
  'details',
  'how-pulse-irrigation',
  'how-pulse-drip',
  'operating-modes',
  'what-pulselogic-measures',
  'what-radsum-measures',
  'pulselogic-cta',
  'radsum-cta',
  'team',
  'mission',
  'highlights',
  'form',
];

export default function ScrollHashSync({
  sectionIds = DEFAULT_SECTION_IDS,
}: {
  sectionIds?: string[];
}) {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (!sections.length) return;

    let currentId = window.location.hash?.replace('#', '') ?? '';

    const observer = new IntersectionObserver(
      (entries) => {
        // Don't change the hash while a modal is open
        if (document.body.dataset.modalOpen === 'true') return;

        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (!visible.length) return;

        const topMost = visible[0].target as HTMLElement;
        const id = topMost.id;

        if (!id || id === currentId) return;

        currentId = id;
        const { pathname, search } = window.location;
        window.history.replaceState(null, '', `${pathname}${search}#${id}`);
      },
      { threshold: 0.5 },
    );

    sections.forEach((section) => observer.observe(section));

    const handleScroll = () => {
      if (document.body.dataset.modalOpen === 'true') return;
      // When near the very top of the page, clear the hash so the URL is just the base path
      if (window.scrollY <= 10 && window.location.hash) {
        currentId = '';
        const { pathname, search } = window.location;
        window.history.replaceState(null, '', `${pathname}${search}`);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [sectionIds]);

  return null;
}

